import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  inject,
} from '@angular/core';
import { ChatActivityStatus } from '../services/chatbot-ia.service';
import {
  inferThinkingTab,
  thinkingCollapsedLiveLabel,
  thinkingDoneLabel,
  thinkingTraceRows,
  ThinkingTraceRow,
} from './chatbot-thinking-trace';
import { faviconUrlForHost } from './chatbot-web-favicon';

@Component({
  selector: 'app-chatbot-thinking-trace',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './chatbot-thinking-trace.component.html',
  styleUrl: './chatbot-thinking-trace.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatbotThinkingTraceComponent implements OnChanges, OnDestroy {
  private cdr = inject(ChangeDetectorRef);

  readonly wosiMarkSrc = '/assets/images/inteligencia-wosi.png';

  @Input({ required: true }) activityLog: ChatActivityStatus[] = [];
  @Input() currentActivity: ChatActivityStatus | null = null;
  @Input() isActive = false;
  @Input() startedAt: number | null = null;
  @Input() settledDurationSeconds: number | null = null;
  @Input() expandByDefault = false;

  expanded = false;
  durationSeconds = 0;
  private userToggledExpand = false;
  private tickTimer?: ReturnType<typeof setInterval>;
  private faviconFailures = new Set<string>();

  ngOnDestroy(): void {
    if (this.tickTimer) {
      clearInterval(this.tickTimer);
      this.tickTimer = undefined;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['settledDurationSeconds'] && this.settledDurationSeconds) {
      this.durationSeconds = this.settledDurationSeconds;
    }

    if (changes['isActive'] || changes['startedAt']) {
      this.syncDurationTimer();
    }

    const turnRestarted =
      (changes['isActive']?.currentValue === true &&
        changes['isActive']?.previousValue === false) ||
      (this.isActive && Boolean(changes['startedAt']));
    if (turnRestarted) {
      this.userToggledExpand = false;
    }

    if (!this.userToggledExpand) {
      this.expanded = this.expandByDefault;
    }

    if (changes['isActive'] && this.isActive && !this.startedAt) {
      this.durationSeconds = 0;
    }

    if (changes['isActive'] && !this.isActive) {
      if (this.settledDurationSeconds) {
        this.durationSeconds = this.settledDurationSeconds;
      } else if (this.startedAt) {
        this.durationSeconds = Math.max(
          1,
          Math.round((Date.now() - this.startedAt) / 1000)
        );
      }
      this.cdr.markForCheck();
    }

    this.cdr.markForCheck();
  }

  get rows() {
    return thinkingTraceRows(
      this.activityLog,
      this.currentActivity,
      this.isActive
    );
  }

  get headerLabel(): string {
    if (this.isActive) {
      return thinkingCollapsedLiveLabel(
        this.activityLog,
        this.currentActivity
      );
    }
    const phase = inferThinkingTab(this.activityLog, this.currentActivity);
    return thinkingDoneLabel(phase, this.durationSeconds);
  }

  toggleExpanded(event: Event): void {
    event.preventDefault();
    this.userToggledExpand = true;
    this.expanded = !this.expanded;
  }

  trackRow(_index: number, row: { id: string }): string {
    return row.id;
  }

  isInlineActionRow(row: ThinkingTraceRow): boolean {
    return row.kind === 'search' || row.kind === 'tool';
  }

  siteFavicon(row: ThinkingTraceRow): string | undefined {
    if (row.kind !== 'search' || this.faviconFailures.has(this.faviconKey(row))) {
      return undefined;
    }
    return faviconUrlForHost(row.siteHost);
  }

  onSiteFaviconError(row: ThinkingTraceRow): void {
    const key = this.faviconKey(row);
    if (this.faviconFailures.has(key)) return;
    this.faviconFailures.add(key);
    this.cdr.markForCheck();
  }

  private faviconKey(row: ThinkingTraceRow): string {
    return `${row.id}:${row.siteHost ?? ''}`;
  }

  private syncDurationTimer(): void {
    if (this.tickTimer) {
      clearInterval(this.tickTimer);
      this.tickTimer = undefined;
    }
    if (!this.isActive || !this.startedAt) return;
    this.tickTimer = setInterval(() => {
      this.durationSeconds = Math.max(
        0,
        Math.round((Date.now() - this.startedAt!) / 1000)
      );
      this.cdr.markForCheck();
    }, 400);
  }
}
